import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileCheck, AlertTriangle, CheckCircle, Clock, FileX } from 'lucide-react';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip } from 'recharts';
import { usePagination } from '@/hooks/usePagination';
import { ListPagination } from '@/components/ListPagination';
import { driverProfileName } from '@/lib/driverProfile';
import { coverExpiryStatus } from '@/lib/coverExpiry';

const COLORS = ['hsl(142, 76%, 36%)', 'hsl(45, 93%, 47%)', 'hsl(0, 72%, 51%)'];

/** The fields read off one `get_contracts_with_status` row (its JSON shape, built in SQL). */
interface ContractInProgress {
    id: number;
    vehicle_id: number | null;
    drivers: { profiles: { first_name: string | null; last_name: string | null } | null } | null;
    vehicles: { make: string | null; model: string | null; vehicle_registrations: { number: string | null } | null } | null;
}

export default function RoadworthyAnalytics() {
    const { data: summaryData, isLoading: summaryLoading } = useQuery({
        queryKey: ['roadworthy-analytics-summary'],
        queryFn: async () => {
            const { data: certificates, error } = await supabase
                .from('vehicle_roadworthiness')
                .select('id, expires_at');
            if (error) throw error;

            let valid = 0;
            let expiringSoon = 0;
            let expired = 0;

            // Good through its expiry date, on the Ghana day (G6): the same rule as the list and the contract tab.
            certificates?.forEach(cert => {
                const status = coverExpiryStatus(cert.expires_at);
                if (status === 'expired') {
                    expired++;
                } else if (status === 'expiring') {
                    expiringSoon++;
                } else {
                    valid++;
                }
            });

            const total = certificates?.length || 0;

            return {
                total,
                valid,
                expiringSoon,
                expired,
                statusData: [
                    { name: 'Valid', value: valid },
                    { name: 'Expiring Soon', value: expiringSoon },
                    { name: 'Expired', value: expired },
                ].filter(d => d.value > 0),
            };
        },
    });

    // Cars on a contract in progress with no roadworthy certificate on file. A car with no
    // certificate has no row in vehicle_roadworthiness, so it cannot come from the certificate
    // counts above: the cars come from the contracts list's own RPC (status = the contract's latest
    // contract_updates row), and a car is "none on file" when no certificate carries its vehicle_id.
    const { data: noneOnFileData, isLoading: noneOnFileLoading } = useQuery({
        queryKey: ['roadworthy-analytics-none-on-file'],
        queryFn: async () => {
            const { data: first, error: countError } = await supabase.rpc('get_contracts_with_status', {
                status_filter: 'IN_PROGRESS',
                page_from: 0,
                page_to: 0,
            });
            if (countError) throw countError;
            const count = (first as unknown as { count: number } | null)?.count ?? 0;

            let contracts: ContractInProgress[] = [];
            if (count > 0) {
                const { data: all, error: contractsError } = await supabase.rpc('get_contracts_with_status', {
                    status_filter: 'IN_PROGRESS',
                    page_from: 0,
                    page_to: count - 1,
                });
                if (contractsError) throw contractsError;
                contracts = (all as unknown as { data: ContractInProgress[] } | null)?.data ?? [];
            }

            const { data: certificates, error: certificatesError } = await supabase
                .from('vehicle_roadworthiness')
                .select('vehicle_id');
            if (certificatesError) throw certificatesError;
            const vehiclesWithCertificate = new Set((certificates ?? []).map(cert => cert.vehicle_id));

            const carsOnContract = new Set<number>();
            const noneOnFile: ContractInProgress[] = [];
            contracts.forEach(contract => {
                if (contract.vehicle_id === null || carsOnContract.has(contract.vehicle_id)) return;
                carsOnContract.add(contract.vehicle_id);
                if (!vehiclesWithCertificate.has(contract.vehicle_id)) noneOnFile.push(contract);
            });

            return { carsOnContract: carsOnContract.size, noneOnFile };
        },
    });

    const {
        paginatedData: paginatedNoneOnFile,
        currentPage: noneOnFilePage,
        totalPages: noneOnFileTotalPages,
        totalItems: noneOnFileTotalItems,
        goToPage: goToNoneOnFilePage,
    } = usePagination({ data: noneOnFileData?.noneOnFile, itemsPerPage: 10 });

    if (summaryLoading || noneOnFileLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Certificates</CardTitle>
                        <FileCheck className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{summaryData?.total || 0}</div>
                        <p className="text-xs text-muted-foreground">Roadworthy certificates, all cars</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Valid</CardTitle>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-green-600">{summaryData?.valid || 0}</div>
                        <p className="text-xs text-muted-foreground">Certificates currently valid</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
                        <Clock className="h-4 w-4 text-yellow-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-yellow-600">{summaryData?.expiringSoon || 0}</div>
                        <p className="text-xs text-muted-foreground">Certificates expiring within 30 days</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Expired</CardTitle>
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-red-600">{summaryData?.expired || 0}</div>
                        <p className="text-xs text-muted-foreground">Certificates needing renewal</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">None on File</CardTitle>
                        <FileX className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{noneOnFileData ? noneOnFileData.noneOnFile.length : '—'}</div>
                        <p className="text-xs text-muted-foreground">
                            {noneOnFileData ? `Of ${noneOnFileData.carsOnContract} cars on contract` : 'Cars on contract'}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Chart */}
            <Card className="border border-border/40">
                <CardHeader>
                    <CardTitle>Certificate Status Distribution</CardTitle>
                    <CardDescription>Breakdown of roadworthy certificate statuses</CardDescription>
                </CardHeader>
                <CardContent>
                    {summaryData?.statusData && summaryData.statusData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={summaryData.statusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={80}
                                    outerRadius={120}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {summaryData.statusData.map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Legend />
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                            No roadworthy certificates found
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Cars on contract with no certificate on file */}
            <Card className="border border-border/40">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileX className="h-5 w-5" />
                        No Certificate on File
                    </CardTitle>
                    <CardDescription>Cars on a contract in progress with no roadworthy certificate recorded</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {!noneOnFileData ? (
                        <p className="text-center text-muted-foreground py-8">Couldn't load the cars on contract</p>
                    ) : noneOnFileData.noneOnFile.length > 0 ? (
                        <>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Vehicle</TableHead>
                                        <TableHead>Driver</TableHead>
                                        <TableHead>Contract</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedNoneOnFile.map((contract) => {
                                        const regNumber = contract.vehicles?.vehicle_registrations?.number;
                                        const driverName = driverProfileName(contract.drivers?.profiles) ?? 'N/A';
                                        return (
                                            <TableRow key={contract.id}>
                                                <TableCell>
                                                    <div>
                                                        <span className="font-medium">{regNumber || 'Unregistered'}</span>
                                                        <p className="text-xs text-muted-foreground">{contract.vehicles?.make} {contract.vehicles?.model}</p>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{driverName}</TableCell>
                                                <TableCell>
                                                    <Link
                                                        to={`/dashboard/contracts/${contract.id}?tab=roadworthiness`}
                                                        className="text-primary hover:underline"
                                                    >
                                                        #{contract.id}
                                                    </Link>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            <ListPagination
                                currentPage={noneOnFilePage}
                                totalPages={noneOnFileTotalPages}
                                onPageChange={goToNoneOnFilePage}
                                totalItems={noneOnFileTotalItems}
                            />
                        </>
                    ) : (
                        <p className="text-center text-muted-foreground py-8">Every car on contract has a roadworthy certificate on file</p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
